import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bell, BellOff, CheckCheck, RefreshCw } from 'lucide-react'
import { apiRequest } from '../api/client'
import { Button } from '../components/ui'
import { errorMessage } from '../utils/format'
import { connectPush, disconnectPush, hasPushSubscription, supportsPush } from '../notifications/push'
import './notifications.css'

type Notice = { id: string; title: string; body: string; url: string; createdAt: string; readAt: string | null }
type Inbox = { data: Notice[]; meta: { total: number; unread: number } }
type DeliveryStatus = 'queued' | 'retrying' | 'provider_accepted' | 'failed' | 'unavailable'

export function NotificationsPage() {
  const [inbox, setInbox] = useState<Inbox>({ data: [], meta: { total: 0, unread: 0 } })
  const [config, setConfig] = useState({ enabled: false, publicKey: '' })
  const [subscribed, setSubscribed] = useState(false)
  const [page, setPage] = useState(1)
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)
  const [settingsLoading, setSettingsLoading] = useState(true)
  const [inboxError, setInboxError] = useState('')
  const [settingsError, setSettingsError] = useState('')
  const [actionError, setActionError] = useState('')
  const [testId, setTestId] = useState<string | null>(null)
  const [delivery, setDelivery] = useState<DeliveryStatus | null>(null)
  const mounted = useRef(false)
  const requests = useRef({ inbox: 0, settings: 0 })

  useEffect(() => {
    mounted.current = true
    const counters = requests.current
    return () => { mounted.current = false; ++counters.inbox; ++counters.settings }
  }, [])

  const refreshInbox = useCallback(async () => {
    const request = ++requests.current.inbox
    try {
      const value = await apiRequest<Inbox>(`/notifications?page=${page}`, { rawResponse: true })
      if (mounted.current && request === requests.current.inbox) { setInbox(value); setInboxError('') }
    } catch (caught) {
      if (mounted.current && request === requests.current.inbox) setInboxError(errorMessage(caught))
    } finally {
      if (mounted.current && request === requests.current.inbox) setLoading(false)
    }
  }, [page])

  const refreshSettings = useCallback(async () => {
    const request = ++requests.current.settings
    try {
      const value = await apiRequest<{ enabled: boolean; publicKey: string }>('/notifications/push/config')
      const active = value.enabled && await hasPushSubscription()
      if (mounted.current && request === requests.current.settings) { setConfig(value); setSubscribed(active); setSettingsError('') }
    } catch (caught) {
      if (mounted.current && request === requests.current.settings) setSettingsError(errorMessage(caught))
    } finally {
      if (mounted.current && request === requests.current.settings) setSettingsLoading(false)
    }
  }, [])

  useEffect(() => {
    const refresh = () => { void refreshInbox(); void refreshSettings() }
    const onVisible = () => { if (document.visibilityState === 'visible') refresh() }
    const initial = window.setTimeout(refresh, 0)
    const timer = window.setInterval(() => { if (navigator.onLine && document.visibilityState === 'visible') refresh() }, 30_000)
    window.addEventListener('online', refresh)
    document.addEventListener('visibilitychange', onVisible)
    return () => { window.clearTimeout(initial); window.clearInterval(timer); window.removeEventListener('online', refresh); document.removeEventListener('visibilitychange', onVisible) }
  }, [refreshInbox, refreshSettings])

  useEffect(() => {
    if (!testId) return
    let cancelled = false
    let timer: number | undefined
    let polls = 0
    async function check() {
      try {
        const value = await apiRequest<{ status: DeliveryStatus }>(`/notifications/${testId}/delivery`)
        if (cancelled) return
        setDelivery(value.status)
        if (value.status !== 'queued' && value.status !== 'retrying') return
      } catch { if (cancelled) return }
      ++polls
      timer = window.setTimeout(() => void check(), polls < 30 ? 2000 : 30_000)
    }
    void check()
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, [testId])

  async function action(run: () => Promise<void>) {
    setBusy(true); setActionError('')
    try { await run() }
    catch (caught) { if (mounted.current) setActionError(errorMessage(caught)) }
    finally { if (mounted.current) setBusy(false) }
  }

  const deliveryMessage = delivery === 'provider_accepted' ? 'Проверка передана службе уведомлений. Проверьте устройство.'
    : delivery === 'failed' ? 'Проверка не отправлена. Включите уведомления заново; если ошибка повторится, обратитесь к администратору.'
    : delivery === 'unavailable' ? 'Для проверки не найдена активная подписка. Включите уведомления заново.'
    : delivery === 'retrying' ? 'Отправка задерживается. Повторная попытка запланирована; лента изменений доступна ниже.'
    : delivery === 'queued' ? 'Проверка ожидает отправки. Дождитесь обновления статуса.' : ''

  return <div className="notifications-page">
    <section className="notification-settings">
      <div><h2>Уведомления на устройство</h2><p>Назначения, переносы, отмены работ, замечания руководителя и назначенные дефекты.</p>
        {!supportsPush() ? <p>Откройте приложение по HTTPS в браузере с поддержкой уведомлений. На iPhone добавьте приложение на экран «Домой» и откройте оттуда.</p>
          : settingsLoading ? <p role="status">Проверяем подключение уведомлений…</p>
          : settingsError ? <p>Не удалось проверить подписку. Нажмите «Обновить» после восстановления подключения.</p>
          : !config.enabled ? <p>Подключение уведомлений на устройство пока недоступно. Все изменения доступны ниже.</p>
          : <p>{subscribed ? 'Включены для этого браузера и вашей учётной записи.' : 'Включите, чтобы получать изменения, когда приложение закрыто.'}</p>}</div>
      <div className="notification-actions">
        {supportsPush() && config.enabled && !settingsLoading && <Button loading={busy} disabled={!!settingsError} onClick={() => void action(async () => {
          if (subscribed) await disconnectPush(); else await connectPush(config.publicKey)
          const active = await hasPushSubscription()
          if (mounted.current) setSubscribed(active)
        })}>{subscribed ? <BellOff size={17} /> : <Bell size={17} />}{subscribed ? 'Отключить' : 'Включить уведомления'}</Button>}
        {subscribed && config.enabled && !settingsError && <Button variant="secondary" disabled={busy} onClick={() => void action(async () => {
          const result = await apiRequest<{ notificationId: string }>('/notifications/push/test', { method: 'POST', body: {} })
          if (mounted.current) { setTestId(result.notificationId); setDelivery('queued') }
          await refreshInbox()
        })}>Проверить доставку</Button>}
      </div>
    </section>
    {actionError && <div className="form-message form-message--error" role="alert">{actionError}</div>}
    {settingsError && <div className="form-message form-message--error" role="alert">Подписка не проверена: {settingsError}</div>}
    {inboxError && <div className="form-message form-message--error" role="alert">Не удалось обновить ленту: {inboxError}</div>}
    {deliveryMessage && <div className={`form-message${delivery === 'failed' || delivery === 'unavailable' ? ' form-message--error' : ''}`} role={delivery === 'failed' || delivery === 'unavailable' ? 'alert' : 'status'}>{deliveryMessage}</div>}
    <div className="notification-toolbar"><h2>Изменения для вас{inbox.meta.unread > 0 ? ` · ${inbox.meta.unread} непрочитанных` : ''}</h2><div className="notification-actions">
      <Button variant="secondary" disabled={busy || !inbox.meta.unread} onClick={() => void action(async () => { await apiRequest('/notifications/read', { method: 'POST', body: {} }); await refreshInbox() })}><CheckCheck size={17} />Прочитано</Button>
      <Button variant="quiet" disabled={busy} onClick={() => { void refreshInbox(); void refreshSettings() }}><RefreshCw size={17} />Обновить</Button></div></div>
    {loading ? <p role="status">Загружаем уведомления…</p>
      : inbox.data.length === 0 ? <div className="notification-settings"><p>{inboxError ? 'Лента пока не загружена. Восстановите подключение и нажмите «Обновить».' : 'Новых изменений нет. Здесь появятся назначения работ и изменения их сроков.'}</p></div>
      : <ol className="notification-list">{inbox.data.map((notice) => <li key={notice.id} className={notice.readAt ? '' : 'notification-unread'}><div><h3><Link to={notice.url.startsWith('/') && !notice.url.startsWith('//') ? notice.url : '/work-orders'}>{notice.title}</Link></h3><time dateTime={notice.createdAt}>{new Date(notice.createdAt).toLocaleString('ru-RU')}</time></div><p>{notice.body.replace(/\n\nОткрыть:[\s\S]*$/, '')}</p></li>)}</ol>}
    {inbox.meta.total > 30 && <div className="notification-actions"><Button variant="secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>Предыдущие</Button><span>Страница {page}</span><Button variant="secondary" disabled={page * 30 >= inbox.meta.total} onClick={() => setPage(page + 1)}>Следующие</Button></div>}
  </div>
}
